// ---------- scene boilerplate ----------
// The renderer, scene, camera, fog, lights and viewer floor — the stage every
// other system plays on. Per-tier costs (DPR cap, render scale, shadow size
// and cadence, light count) are decided here, once, at boot.
import * as THREE from 'three';
import { TIER, LOW, ULTRA_LOW_SHADOWS } from './tier.js';
import { TUNING } from './tuning.js';
import { PALETTE } from './palette.js';

const app = document.getElementById('app');
export const scene = new THREE.Scene();
scene.background = new THREE.Color(PALETTE.skyCream);

export const camera = new THREE.PerspectiveCamera(35, innerWidth/innerHeight, 0.1, TUNING.cameraFar ?? 100);
camera.position.set(0, 1.4, 4.2);

// AA off on LOW (huge fillrate save on UHD 600); on elsewhere so high-DPI
// phones/laptops get smooth edges. stencil/alpha off (we never use them).
// powerPreference 'low-power' picks integrated GPU intentionally on hybrid laptops.
export const renderer = new THREE.WebGLRenderer({
  antialias: !LOW,
  powerPreference: LOW ? 'low-power' : 'high-performance',
  stencil: false,
  alpha: false,
});
// DPR is the #1 fillrate lever. Cap at 1.0 on low tier (Chromebook), and on top
// of that render the framebuffer below CSS size (RENDER_SCALE) and let the
// browser upscale — the single biggest fillrate save on weak integrated GPUs.
// Off LOW we render at native DPR (cap 3): a capped buffer on 2×/3× Retina phones
// is exactly what reads as "pixelated," and iOS Safari always lands on mid tier
// (it hides deviceMemory + GPU strings). The runtime governor (main.js) throttles
// the ratio back down on any non-low device that can't sustain native res.
const RENDER_SCALE = TUNING.renderScale ?? (LOW ? 0.67 : 1.0);
renderer.setPixelRatio(Math.min(devicePixelRatio, TUNING.dprCap ?? (LOW ? 1.0 : 3)) * RENDER_SCALE);
renderer.setSize(innerWidth, innerHeight);
renderer.shadowMap.enabled = !ULTRA_LOW_SHADOWS;
renderer.shadowMap.type = LOW ? THREE.PCFShadowMap : THREE.PCFSoftShadowMap;
// Shadows are the second full geometry pass each frame (every caster re-rendered
// into the depth map). The sun is static and the only movers — Chocky and the
// wolves — shift only a hair between frames, so refreshing the shadow map every
// other frame on weak GPUs is visually indistinguishable and roughly halves the
// shadow cost. We drive it by hand instead of letting three.js redo it at full
// rate; needsUpdate auto-clears after each shadow render.
renderer.shadowMap.autoUpdate = false;
renderer.shadowMap.needsUpdate = true;          // seed the first frame
export const SHADOW_INTERVAL = TUNING.shadowInterval ?? (ULTRA_LOW_SHADOWS ? 0 : (LOW ? 2 : 1)); // frames between shadow refreshes
renderer.outputColorSpace = THREE.SRGBColorSpace;
// Filmic tone mapping rolls bright highlights (sunlit terracotta roofs, lantern
// glass, water glints) into a smooth shoulder instead of clipping them to flat
// white, which reads as more depth and a warmer storybook contrast. It's a
// single per-pixel op folded into the existing output shader — negligible even
// on a mid-tier phone — but we keep it OFF on the low tier so the Chromebook
// output stays byte-identical to the hand-tuned palette and pays literally
// nothing. Exposure is lifted a hair to counter ACES's midtone dip so the
// pastel palette keeps its brightness.
renderer.toneMapping = LOW ? THREE.NoToneMapping : THREE.ACESFilmicToneMapping;
renderer.toneMappingExposure = LOW ? 1 : 1.12;
app.appendChild(renderer.domElement);

let _contextLost = false;   // set by webglcontextlost; pauses the main loop
export function isContextLost() { return _contextLost; }

// WebGL context-loss handling. ChromeOS fires this on GPU reset / memory
// pressure / backgrounding; without preventDefault() the browser will NOT
// restore the context and the canvas stays permanently black (looks like a
// crash). The whole scene is built procedurally at load (instanced meshes,
// canvas textures, the GLB), so the simplest *robust* recovery is a full
// reload rather than hand-rebuilding every GPU resource — fine for a
// kiosk/education context. (In-place rebuild belongs with a warm-hub / shared-
// context architecture, which is out of scope here.)
renderer.domElement.addEventListener('webglcontextlost', (e) => {
  e.preventDefault();           // allow the browser to attempt restoration
  _contextLost = true;          // pause the main loop (see tick())
  const o = document.getElementById('contextLostOverlay');
  if (o) o.style.display = 'grid';
  console.warn('[perf] WebGL context lost — pausing, awaiting restore');
}, false);
renderer.domElement.addEventListener('webglcontextrestored', () => {
  console.warn('[perf] WebGL context restored — reloading to rebuild scene');
  location.reload();
}, false);

let resizePending = false;
addEventListener('resize', () => {
  if (resizePending) return;
  resizePending = true;
  requestAnimationFrame(() => {
    resizePending = false;
    camera.aspect = innerWidth / innerHeight;
    camera.updateProjectionMatrix();
    renderer.setSize(innerWidth, innerHeight);
  });
});

// Exponential-squared fog: a denser, more natural atmospheric haze than
// linear fog — distant scenery dissolves into the sky with a soft, gradual
// falloff instead of a hard near/far band. Density is hand-tuned so the
// village (~28 units across) stays crisp while the wolf-haunted plain beyond
// melts convincingly into the mist. The day-night cycle keeps .color in sync
// with the sky, so the haze tints from pale-gold noon to deep-blue night.
scene.fog = new THREE.FogExp2(PALETTE.skyCream, TUNING.fogDensity ?? 0.019);
export const FOG_BASE_DENSITY = scene.fog.density;

// lights — three-light setup tuned for "warm sunny afternoon"
export const hemi = new THREE.HemisphereLight(PALETTE.skyBlueSoft, PALETTE.sand, 0.55);   // soft sky + warm ground bounce
scene.add(hemi);

// THE SUN: warm directional light. Shadow map size + frustum scaled per tier.
export const key = new THREE.DirectionalLight(PALETTE.panel, 2.2);
key.position.set(6, 10, 5);
key.castShadow = !ULTRA_LOW_SHADOWS;
// Genuinely capable machines (the 'high' tier: plenty of RAM, cores and a big
// texture cap) get a 4K depth map for crisp, clean shadow edges — free on that
// class of GPU. Mid stays 2K, low stays 1K.
const SHADOW_SIZE = TUNING.shadowSize ?? (LOW ? 1024 : (TIER === 'high' ? 4096 : 2048));
key.shadow.mapSize.set(SHADOW_SIZE, SHADOW_SIZE);
// A ±12 ortho box (24 units) — wide enough to cover the village (±9) when
// centred on the origin, and the play loop slides it to stay centred on Chocky
// (updateShadowFollow in main.js) so his shadow survives out on the surrounding
// hills. Tightened from ±16: packing the same depth map into a smaller footprint
// raises the effective shadow resolution ~1.8× for zero extra cost — sharper
// contact edges under Chocky, the houses and the trees. Re-aiming the frustum is
// free: the map already refreshes on its own cadence (SHADOW_INTERVAL) because
// Chocky and the wolves move every frame regardless.
key.shadow.camera.left = -12;
key.shadow.camera.right = 12;
key.shadow.camera.top = 12;
key.shadow.camera.bottom = -12;
key.shadow.camera.near = 0.5;
key.shadow.camera.far = 40;
key.shadow.bias = -0.0003;
key.shadow.normalBias = 0.02;
key.shadow.radius = LOW ? 2 : 4;
scene.add(key);
scene.add(key.target);
// Sky bounce fill (kept on all tiers — single extra light, cheap)
export const fill = new THREE.DirectionalLight(PALETTE.skyBlueSoft, LOW ? 0.5 : 0.35);
fill.position.set(-4, 3, -3);
scene.add(fill);

// Rim light — dropped on low tier (3 lights → 2 = noticeable shader savings on UHD)
export const rim = new THREE.DirectionalLight(0xffffff, 0.45);
rim.position.set(-2, 4, -6);
if (!LOW) scene.add(rim);

// floor — circle for editor view, big plane for play mode
export const floor = new THREE.Mesh(
  new THREE.CircleGeometry(4, 64),
  new THREE.ShadowMaterial({ opacity: 0.15 })
);
floor.rotation.x = -Math.PI / 2;
floor.receiveShadow = true;
scene.add(floor);
